// src/pages/ArgusPage.jsx
// Argus page - a stopgap until the Cameras tab (camera plan Tasks 17-18). Admins, plus the
// read-only sales demo account, which the backend gives only its tower's lenses and no
// server-admin address (url null hides the "Server admin" link below).
// Live lenses play straight from Sentari: GET /api/argus-ui/monitors/:id/hls/* passes the
// camera server's video through with its key kept server-side, so there is no second
// login and nothing to leak in the browser. The camera server's own admin screen is a
// separate "Open Argus" link - it opens in a new tab (Sentari is https, that screen is
// plain http on Tailscale, which browsers refuse to frame) and works only on Tailscale.
import { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { Video, ExternalLink, RefreshCw } from 'lucide-react';
import apiService from '../services/apiService';
import { theme, rgba } from '../theme';

const tileStyle = {
  borderRadius: '12px', overflow: 'hidden',
  background: rgba(theme.colors.tileBackgroundRgb, 0.8),
  border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
};

function LivePlayer({ monitorId }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !Hls.isSupported()) { setFailed(true); return undefined; }
    let retriedAuth = false;
    let hls;
    const start = () => {
      hls = new Hls({
        liveSyncDurationCount: 2,
        // The token travels as a header, never in the URL.
        xhrSetup: (xhr) => {
          const token = apiService.getAccess();
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        },
      });
      hls.on(Hls.Events.ERROR, async (_e, data) => {
        if (!data.fatal) return;
        hls.destroy();
        // An expired sign-in token: refresh it once and try again.
        if (data.response?.code === 401 && !retriedAuth) {
          retriedAuth = true;
          if (await apiService.refreshAccessToken()) { start(); return; }
        }
        setFailed(true);
      });
      hls.loadSource(`${apiService.baseURL}/api/argus-ui/monitors/${encodeURIComponent(monitorId)}/hls/s.m3u8`);
      hls.attachMedia(video);
    };
    start();
    return () => { if (hls) hls.destroy(); };
  }, [monitorId]);

  if (failed) return <div style={{ padding: '40px 12px', textAlign: 'center', color: '#94a3b8' }}>Video not available</div>;
  return <video ref={videoRef} muted autoPlay playsInline controls style={{ width: '100%', display: 'block', background: '#000', aspectRatio: '16 / 9' }} />;
}

export default function ArgusPage() {
  const [adminUrl, setAdminUrl] = useState(null);
  const [monitors, setMonitors] = useState(null);
  const [error, setError] = useState(null);

  const loadMonitors = () => {
    setError(null);
    apiService.request('/api/argus-ui/monitors')
      .then(d => setMonitors(d?.monitors || []))
      .catch(() => { setMonitors([]); setError('The camera server is not reachable right now.'); });
  };

  useEffect(() => {
    let live = true;
    apiService.request('/api/argus-ui')
      .then(d => { if (live) setAdminUrl(d?.available ? d.url : null); })
      .catch(() => {});
    loadMonitors();
    return () => { live = false; };
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <h2 style={{ color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
          <Video size={22} /> Argus
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={loadMonitors} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px',
            border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.5)}`, background: 'transparent', color: '#cbd5e1', cursor: 'pointer',
          }}>
            <RefreshCw size={14} /> Refresh
          </button>
          {adminUrl && (
            <a href={adminUrl} target="_blank" rel="noopener noreferrer" title="Camera server admin screen - Tailscale only" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px',
              textDecoration: 'none', background: '#3b82f6', color: '#fff', fontWeight: 600,
            }}>
              Server admin <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      {monitors === null && <div style={{ color: '#94a3b8' }}>Loading cameras...</div>}
      {error && <div style={{ color: '#f59e0b', marginBottom: '12px' }}>{error}</div>}
      {monitors && monitors.length === 0 && !error && <div style={{ color: '#94a3b8' }}>No cameras set up yet.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
        {(monitors || []).map(m => (
          <div key={m.id} style={tileStyle}>
            {m.live
              ? <LivePlayer monitorId={m.id} />
              : <div style={{ padding: '40px 12px', textAlign: 'center', color: '#64748b', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Off</div>}
            <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600 }}>{m.name}</span>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
                background: m.live ? 'rgba(34,197,94,0.18)' : 'rgba(100,116,139,0.18)', color: m.live ? '#22c55e' : '#94a3b8',
              }}>{m.live ? 'LIVE' : 'OFF'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
